<script setup lang="ts">
import { ut_getText, ut_navigateNewRoute } from '@base/utils/utils'
import { ut_wilo_getSpaixDefaultDutyPoint, ut_wilo_getSpaixDefaultUnits } from '~/utils/utils'
import type * as tp from '@base/utils/types'
import type * as pfWilo from '@amirada/lib-wilo-shared'
import { PARAM } from '@amirada/lib-wilo-shared'
import { FlowUnits, HeadUnits, PressureUnits } from '~/utils/constants'

const p = defineProps<{
  isDetail?: boolean
  realCountry: string
  hideButton?: boolean
  isCartItem?: boolean
  isQuickSelect?: boolean
  setUrl?: boolean
  dutyPointInput?: pfWilo.IDutyPointInput
}>()

const emit = defineEmits<{ 'update:dutyPoint': [pfWilo.IDutyPointInput] }>()

const enum type {
  flow = 0,
  head = 1,
  staticHead = 2,
}

const dutyPointInputs: Ref<number[]> = ref([])
const unitInputs: Ref<string[]> = ref([])
const inputs: string[] = ['flow', 'head', 'static-head']
const inputsShort: string[] = ['Q', 'H', '']

onBeforeMount(() => {
  // Check if dutyPointInput is already set otherwise default
  const dutyPointInput: pfWilo.IDutyPointInput = p.dutyPointInput && p.dutyPointInput.DutyPoint && p.dutyPointInput.SelectedUnits
    ? p.dutyPointInput
    : { SelectedUnits: ut_wilo_getSpaixDefaultUnits(p.realCountry), DutyPoint: ut_wilo_getSpaixDefaultDutyPoint() }
  convertToInputs(dutyPointInput)
})

// Watch change happens if called from cart item
watch(() => p.dutyPointInput, () => {
  if (p.dutyPointInput) {
    convertToInputs(p.dutyPointInput)
  }
})

// Convert data to input and select fields
function convertToInputs(dutyPointInput: pfWilo.IDutyPointInput) {
  const dutyPoint = dutyPointInput.DutyPoint
  const selectedUnits = dutyPointInput.SelectedUnits
  if (selectedUnits && dutyPoint) {
    const headAsPressure: boolean = !!dutyPoint?.HeadAsPressure
    dutyPointInputs.value = [
      dutyPoint.Flow || 0,
      headAsPressure ? dutyPoint.HeadAsPressure || 0 : dutyPoint.Head || 0,
      dutyPoint.StaticHead || 0,
    ]
    unitInputs.value = [
      selectedUnits.UnitFlow,
      headAsPressure ? selectedUnits.UnitPressure : selectedUnits.UnitHead,
      selectedUnits.UnitHead,
    ]
  }
}

// return array for UI
function getUnitItems(index: number): tp.ISelectItem[] {
  const list: string[] = index === type.flow ? FlowUnits : index === type.head ? HeadUnits.concat(PressureUnits) : HeadUnits
  const res: tp.ISelectItem[] = []
  for (const item of list) {
    res.push({
      value: item,
      label: item,
    })
  }
  return res
}

function headAsPressure(): boolean {
  return PressureUnits.includes(unitInputs.value[type.head] || '')
}

// Sync head and static head unit
function syncUnit(index: number): void {
  if (index === type.flow) { return }
  if (!headAsPressure()) {
    if (index === type.head) {
      unitInputs.value[type.staticHead] = unitInputs.value[type.head] || ''
    }
    else {
      unitInputs.value[type.head] = unitInputs.value[type.staticHead] || ''
    }
  }
}

// Emit duty point
function emitDutyPoint(): void {
  const newUnits: pfWilo.TSelectedUnits = {
    UnitFlow: unitInputs.value[type.flow] || '',
    UnitHead: unitInputs.value[type.staticHead] || '',
    UnitPressure: headAsPressure() ? unitInputs.value[1] || '' : ut_wilo_getSpaixDefaultUnits(p.realCountry).UnitPressure,
  }
  const newDutyPoint: pfWilo.TDutyPoint = {
    Flow: dutyPointInputs.value[type.flow] || 0,
    Head: !headAsPressure() ? dutyPointInputs.value[type.head] || 0 : 0,
    StaticHead: dutyPointInputs.value[type.staticHead] || 0,
    HeadAsPressure: headAsPressure() ? dutyPointInputs.value[type.head] || 0 : 0,
  }
  const dutyPointInput: pfWilo.IDutyPointInput = { SelectedUnits: newUnits, DutyPoint: newDutyPoint }
  emit('update:dutyPoint', dutyPointInput)
  if (p.setUrl) {
    ut_navigateNewRoute('', { [PARAM.spaixSettings]: dutyPointInput }, '', false)
  }
}
</script>

<template>
  <ClientOnly>
    <template v-for="(input, index) in inputs" :key="input">
      <div v-if="index < 2 || isDetail" :class="!isCartItem ? 'mb-6' : ''">
        <label v-if="!isCartItem" class="font-bold" v-html="ut_getText('txt_' + input)" />
        <div class="flex flex-wrap gap-2 align-items-center" :class="!isCartItem ? 'mt-2' : ''">
          <label v-if="isCartItem" class="font-bold mt-1" v-html="inputsShort[index] + ':'" />
          <UInput
            v-model="dutyPointInputs[index]"
            type="number"
            min="0"
            max="10000"
            step="0.000001"
            @change="isCartItem ? emitDutyPoint(): ''"
          />
          <USelect
            v-model="unitInputs[index]"
            class="w-30"
            :items="getUnitItems(index)"
            @change="isCartItem ? emitDutyPoint(): syncUnit(index)"
          />
        </div>
      </div>
    </template>

    <div class="flex" :class="p.isDetail ? 'mt-6' : 'mb-6'">
      <UButton
        v-if="!hideButton"
        class="mt-auto"
        icon="i-wilo-wilo-pump"
        :label="ut_getText(!isQuickSelect ? 'txt_set-duty-point': 'txt_qs-search')"
        :disabled="!(dutyPointInputs[type.flow] && dutyPointInputs[type.head])"
        @click="emitDutyPoint()"
      />
    </div>
  </ClientOnly>
</template>
